/**
 * Opponent Cards Layout for Poker Reborn
 *
 * Displays an opponent's hole cards with compact styling.
 * Supports variable card count (2 for Hold'em, 4 for Omaha).
 * Small-sized cards with tight spacing and subtle fan effect.
 *
 * @see docs/screens/room/CARDS_OPPONENT.md - Opponent cards specs, animations
 * @see docs/screens/room/CARD_VIEW.md - CardView component reference
 */

import { Container, Graphics } from 'pixi.js';
import { CardView, CARD_STYLES } from './CardView';
import { DEALING_CONFIG } from '@/config/dealingAnimation';
import type { Card } from '@/types/game';
import { getScaledValue } from '@/utils/responsive';

// =============================================================================
// OPPONENT LAYOUT CONFIG
// =============================================================================

const LAYOUT = {
  /** Card size for opponents */
  cardSize: 'small' as const,

  /** Spacing multiplier (relative to card width) - tighter overlap */
  spacingMultiplier: 0.5,

  /** Tighter spacing for 4-card (Omaha) layout — same total spread as 2 cards */
  spacingMultiplier4: 0.180,

  /** Fan rotation range (radians) — cards interpolate from -maxRotation to +maxRotation */
  maxRotation: 0.05, // ~3 degrees
};

// =============================================================================
// BREAKPOINT-BASED SCALING (BBS)
// =============================================================================

/**
 * BBS: Card width at specific screen widths.
 * Add/remove entries as needed - interpolation is automatic.
 *
 * @see docs/screens/room/CARDS_OPPONENT.md - Breakpoint-Based Scaling section
 * @see docs/systems/BBS.md - Unified responsive system
 */
const OPPONENT_CARD_WIDTH_BREAKPOINTS: Record<number, number> = {
  1920: 64,
  1440: 42,
  720: 36,
  381: 30,
};

/**
 * BBS: Revealed card width at specific screen widths.
 * When opponent cards are shown/revealed, they scale up to these sizes for readability.
 *
 * @see docs/screens/room/CARDS_OPPONENT.md - Reveal animation section
 */
const OPPONENT_CARD_REVEALED_WIDTH_BREAKPOINTS: Record<number, number> = {
  1920: 90,
  1440: 68,
  720: 38,
  375: 32,
};

/**
 * BBS: Minimum gap between revealed cards at specific screen widths.
 * This defines the actual visible gap between card edges after reveal animation.
 * The code calculates the required offset to achieve this gap.
 *
 * @see docs/screens/room/CARDS_OPPONENT.md - Reveal animation section
 */
const OPPONENT_CARD_REVEAL_GAP_BREAKPOINTS: Record<number, number> = {
  1920: 10,
  1440: 8,
  720: 5,
  375: 4,
};

// =============================================================================
// ANIMATION CONFIG
// =============================================================================

const ANIMATION = {
  /** Deal animation timing - faster for opponents */
  deal: {
    duration: 300,
    stagger: 50,
    arcHeight: 40,
  },

  /** Fold animation timing */
  fold: {
    flipDuration: 150,
    slideDuration: 450,   // Slower for smooth dramatic effect
    fadeDuration: 100,    // Shorter fade (unused - fade is integrated)
    travelDistance: 200,  // How far cards travel toward center
    targetScale: 0.5,     // Shrink to 50%
    targetRotation: Math.PI, // Half rotation (180°)
  },

  /** Reveal animation (uses breakpoints for width and spread) */
  reveal: {
    duration: 250,
    stagger: 60,
  },

  /** Highlight effect - subtler */
  highlight: {
    glowColor: 0xffd700,
    glowAlpha: 0.6,
    pulseFrequency: 2,
    pulseDuration: 1000,
    liftAmount: 6,
  },
};

// =============================================================================
// EASING FUNCTIONS
// =============================================================================

function easeOutPower2(progress: number): number {
  return 1 - Math.pow(1 - progress, 2);
}

function easeInPower2(progress: number): number {
  return progress * progress;
}

// =============================================================================
// HELPER: compute card positions and rotations for N cards
// =============================================================================

function computeOpponentCardLayout(
  cardCount: number,
  spacing: number,
  seatIndex: number
): { positions: number[]; rotations: number[] } {
  const positions: number[] = [];
  const rotations: number[] = [];
  for (let i = 0; i < cardCount; i++) {
    positions.push((i - (cardCount - 1) / 2) * spacing);
    // Base rotation interpolated across cards
    const t = cardCount > 1 ? i / (cardCount - 1) : 0.5;
    const baseRotation = -LAYOUT.maxRotation + 2 * LAYOUT.maxRotation * t;
    // Per-seat random offset for visual variety
    const seed = Math.sin(seatIndex * (7.13 + i * 4.67) + 1.37 + i * 2.31) * 0.5;
    rotations.push(baseRotation + seed * 0.08);
  }
  return { positions, rotations };
}

// =============================================================================
// OPPONENT CARDS LAYOUT
// =============================================================================

export class OpponentCardsLayout extends Container {
  private cards: CardView[] = [];
  private basePositions: number[] = [];
  private baseRotations: number[] = [];
  private spacing: number;
  private seatIndex: number;

  // Responsive scaling
  private currentScale = 1;

  // Glow effect
  private glowGraphics: Graphics;
  private isHighlighted = false;
  private pulseAnimationId: number | null = null;

  // Track original positions for animations
  private originalCardYs: number[] = [];

  constructor(seatIndex = 0, cardCount = 2) {
    super();
    this.label = 'opponentCardsLayout';
    this.seatIndex = seatIndex;

    const dims = CARD_STYLES[LAYOUT.cardSize];
    const multiplier = cardCount > 2 ? LAYOUT.spacingMultiplier4 : LAYOUT.spacingMultiplier;
    this.spacing = dims.width * multiplier;

    // Create glow graphics (behind cards)
    this.glowGraphics = new Graphics();
    this.glowGraphics.visible = false;
    this.addChild(this.glowGraphics);

    this.buildCards(cardCount);
  }

  // ===========================================================================
  // CARD SLOT MANAGEMENT
  // ===========================================================================

  private buildCards(cardCount: number): void {
    for (const card of this.cards) {
      this.removeChild(card);
    }

    const { positions, rotations } = computeOpponentCardLayout(cardCount, this.spacing, this.seatIndex);
    this.basePositions = positions;
    this.baseRotations = rotations;
    this.originalCardYs = new Array<number>(cardCount).fill(0);
    this.cards = [];

    for (let i = 0; i < cardCount; i++) {
      const card = new CardView(LAYOUT.cardSize);
      card.x = positions[i]!;
      card.rotation = rotations[i]!;
      this.addChild(card);
      this.cards.push(card);
    }
  }

  /**
   * Change the number of card slots. Call before dealing a new hand.
   */
  setCardCount(cardCount: number): void {
    if (this.cards.length === cardCount) return;
    this.buildCards(cardCount);
    for (const card of this.cards) {
      card.scale.set(this.currentScale);
    }
    this.recalcSpacing();
  }

  // ===========================================================================
  // PUBLIC METHODS
  // ===========================================================================

  /**
   * Deal cards with animation (faster for opponents)
   */
  async deal(cards: Card[], faceUp: boolean, fromPosition?: { x: number; y: number }): Promise<void> {
    for (let i = 0; i < Math.min(cards.length, this.cards.length); i++) {
      this.cards[i]!.setCard(cards[i]!);
    }
    await this.animateDeal(faceUp, fromPosition);
  }

  /**
   * Reveal cards (flip face up)
   * Cards straighten, scale up, and spread apart during the flip.
   */
  async reveal(cards: Card[]): Promise<void> {
    for (let i = 0; i < Math.min(cards.length, this.cards.length); i++) {
      this.cards[i]!.setCard(cards[i]!);
    }

    const { stagger } = ANIMATION.reveal;
    const screenWidth = window.innerWidth;

    const desiredGap = getScaledValue(screenWidth, OPPONENT_CARD_REVEAL_GAP_BREAKPOINTS, { discrete: false });
    const revealedWidth = getScaledValue(screenWidth, OPPONENT_CARD_REVEALED_WIDTH_BREAKPOINTS, { discrete: false });
    const spreadOffset = (desiredGap + revealedWidth - this.spacing) / 2;

    const promises: Promise<void>[] = [];
    for (let i = 0; i < this.cards.length; i++) {
      const card = this.cards[i]!;
      const startX = this.basePositions[i]!;
      const direction = i < this.cards.length / 2 ? -1 : 1;
      const endX = startX + direction * spreadOffset;
      const delay = i * stagger;
      promises.push(
        new Promise<void>((resolve) => {
          setTimeout(async () => {
            await this.animateRevealCard(card, this.baseRotations[i]!, startX, endX);
            resolve();
          }, delay);
        })
      );
    }
    await Promise.all(promises);
  }

  /**
   * Animate a single card reveal: flip + straighten + scale up + spread
   */
  private animateRevealCard(
    card: CardView,
    startRotation: number,
    startX: number,
    endX: number
  ): Promise<void> {
    return new Promise((resolve) => {
      const { duration } = ANIMATION.reveal;
      const startTime = Date.now();
      const startScale = this.currentScale;

      const screenWidth = window.innerWidth;
      const opponentCardWidth = getScaledValue(screenWidth, OPPONENT_CARD_WIDTH_BREAKPOINTS, { discrete: false });
      const revealedWidth = getScaledValue(screenWidth, OPPONENT_CARD_REVEALED_WIDTH_BREAKPOINTS, { discrete: false });
      const endScale = (revealedWidth * startScale) / opponentCardWidth;

      let contentSwapped = false;

      const animate = (): void => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOutPower2(progress);

        // Straighten rotation (tilt → 0)
        card.rotation = startRotation * (1 - eased);

        // Spread horizontally (move apart)
        card.x = startX + (endX - startX) * eased;

        // Scale Y: currentScale → endScale (eased)
        card.scale.y = startScale + (endScale - startScale) * eased;

        // Scale X: flip effect (currentScale → 0 → endScale)
        if (progress < 0.5) {
          card.scale.x = startScale * (1 - progress * 2);
        } else {
          if (!contentSwapped) {
            contentSwapped = true;
            card.flip(true, false);
          }
          card.scale.x = endScale * ((progress - 0.5) * 2);
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          card.rotation = 0;
          card.x = endX;
          card.scale.set(endScale);
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  /**
   * Fold cards (fly away animation)
   */
  async fold(): Promise<void> {
    await this.animateFold();
  }

  /**
   * Set cards without animation
   */
  setCards(cards: Card[], faceUp = true): void {
    for (let i = 0; i < Math.min(cards.length, this.cards.length); i++) {
      this.cards[i]!.setCard(cards[i]!);
      this.cards[i]!.flip(faceUp, false);
    }
  }

  /**
   * Show card backs (common for opponents) - no animation
   */
  showBacks(): void {
    this.resetCardPositions();
    this.resetHighlights();
    this.visible = true;

    for (const card of this.cards) {
      card.setCard(null);
      card.flip(false, false);
    }
  }

  /**
   * Deal card backs with animation (for opponent dealing)
   */
  async dealBacks(fromPosition?: { x: number; y: number }): Promise<void> {
    this.resetCardPositions();
    this.resetHighlights();
    this.visible = true;

    for (const card of this.cards) {
      card.setCard(null);
    }
    await this.animateDeal(false, fromPosition);
  }

  /**
   * Deal a single card back with animation (for round-robin dealing)
   * @param cardIndex - Card index (0-based)
   * @param fromPosition - Starting position (deck location)
   */
  async dealSingleCardBack(
    cardIndex: number,
    fromPosition?: { x: number; y: number }
  ): Promise<void> {
    const targetCard = this.cards[cardIndex];
    const targetX = this.basePositions[cardIndex];
    const targetRotation = this.baseRotations[cardIndex];
    if (!targetCard || targetX === undefined || targetRotation === undefined) return;

    // Reset highlights and positions when dealing first card (start of new hand)
    if (cardIndex === 0) {
      this.resetCardPositions();
      this.resetHighlights();
      // Hide all cards except first until they're dealt
      for (let i = 1; i < this.cards.length; i++) {
        this.cards[i]!.alpha = 0;
      }
    }

    // Reset card state
    targetCard.setCard(null);
    targetCard.flip(false, false);

    // Animate from start position to final position
    await this.animateSingleCard(targetCard, targetX, targetRotation, fromPosition);
  }

  /**
   * Highlight cards with glow and pulse effect
   */
  highlight(on: boolean): void {
    if (on === this.isHighlighted) return;
    this.isHighlighted = on;

    if (on) {
      this.drawGlow();
      this.glowGraphics.visible = true;

      for (let i = 0; i < this.cards.length; i++) {
        this.originalCardYs[i] = this.cards[i]!.y;
        this.cards[i]!.y -= ANIMATION.highlight.liftAmount;
      }

      this.startPulseAnimation();
    } else {
      this.stopPulseAnimation();
      this.glowGraphics.visible = false;

      for (let i = 0; i < this.cards.length; i++) {
        this.cards[i]!.y = this.originalCardYs[i] ?? 0;
      }
    }
  }

  /**
   * Highlight specific winning cards.
   * Core cards get strong gold highlight, kicker cards get subtle gray highlight.
   */
  highlightWinningCards(coreCards: Card[], kickerCards: Card[] = []): void {
    for (const cardView of this.cards) {
      const cardData = cardView.getCard();

      const isCore =
        cardData &&
        coreCards.some((wc) => wc.rank === cardData.rank && wc.suit === cardData.suit);
      const isKicker =
        cardData &&
        kickerCards.some((wc) => wc.rank === cardData.rank && wc.suit === cardData.suit);

      if (isCore) {
        cardView.highlight(true, 'strong');
        cardView.darken(false);
      } else if (isKicker) {
        cardView.highlight(true, 'subtle');
        cardView.darken(false);
      } else {
        cardView.highlight(false);
        cardView.darken(true);
      }
    }
  }

  /**
   * Reset highlights
   */
  resetHighlights(): void {
    for (const card of this.cards) {
      card.highlight(false);
      card.darken(false);
    }
    this.highlight(false);
  }

  /**
   * Darken cards
   */
  darken(on: boolean): void {
    for (const card of this.cards) {
      card.darken(on);
    }
  }

  /**
   * Get current card data (for state transfer)
   */
  getState(): { card1: Card | null; card2: Card | null; card1FaceUp: boolean; card2FaceUp: boolean } {
    return {
      card1: this.cards[0]?.getCard() ?? null,
      card2: this.cards[1]?.getCard() ?? null,
      card1FaceUp: this.cards[0]?.isFaceUpState() ?? false,
      card2FaceUp: this.cards[1]?.isFaceUpState() ?? false,
    };
  }

  /**
   * Restore state (for state transfer)
   */
  restoreState(state: { card1: Card | null; card2: Card | null; card1FaceUp: boolean; card2FaceUp: boolean }): void {
    if (state.card1 && this.cards[0]) this.cards[0].setCard(state.card1);
    if (state.card2 && this.cards[1]) this.cards[1].setCard(state.card2);
    this.cards[0]?.flip(state.card1FaceUp, false);
    this.cards[1]?.flip(state.card2FaceUp, false);
  }

  /**
   * Reset to initial state
   */
  reset(): void {
    this.resetCardPositions();
    this.resetHighlights();
    for (const card of this.cards) {
      card.setCard(null);
      card.flip(false, false);
    }
  }

  /**
   * Force redraw of card graphics after GPU context loss.
   * @see docs/systems/BACKGROUND_TAB_RECOVERY.md
   */
  redraw(): void {
    for (const card of this.cards) {
      card.redraw();
    }
  }

  /**
   * Nuclear reset - restore ALL card properties to pristine state.
   * Called at hand start to ensure clean slate.
   */
  resetAllCards(): void {
    this.stopPulseAnimation();
    this.isHighlighted = false;
    this.glowGraphics.visible = false;
    this.glowGraphics.clear();

    this.originalCardYs = new Array<number>(this.cards.length).fill(0);

    for (let i = 0; i < this.cards.length; i++) {
      const card = this.cards[i]!;
      card.resetAll();
      card.scale.set(this.currentScale);
      card.x = this.basePositions[i]!;
      card.y = 0;
      card.rotation = this.baseRotations[i]!;
    }
  }

  /**
   * Update layout for screen size (responsive scaling)
   * @param screenWidth - Current screen width in pixels
   */
  updateLayout(screenWidth: number): void {
    const cardWidth = getScaledValue(screenWidth, OPPONENT_CARD_WIDTH_BREAKPOINTS, { discrete: false });
    const baseWidth = CARD_STYLES[LAYOUT.cardSize].width;
    const scale = cardWidth / baseWidth;
    this.currentScale = scale;

    this.recalcSpacing();

    for (let i = 0; i < this.cards.length; i++) {
      this.cards[i]!.scale.set(scale);
      this.cards[i]!.x = this.basePositions[i]!;
    }

    if (this.isHighlighted) {
      this.drawGlow();
    }
  }

  // ===========================================================================
  // PRIVATE - SPACING RECALCULATION
  // ===========================================================================

  private recalcSpacing(): void {
    const dims = CARD_STYLES[LAYOUT.cardSize];
    const multiplier = this.cards.length > 2 ? LAYOUT.spacingMultiplier4 : LAYOUT.spacingMultiplier;
    this.spacing = dims.width * multiplier * this.currentScale;
    const { positions, rotations } = computeOpponentCardLayout(this.cards.length, this.spacing, this.seatIndex);
    this.basePositions = positions;
    this.baseRotations = rotations;
  }

  // ===========================================================================
  // PRIVATE - DEAL ANIMATION
  // ===========================================================================

  private animateDeal(faceUp: boolean, fromPosition?: { x: number; y: number }): Promise<void> {
    return new Promise((resolve) => {
      const { duration, stagger, arcHeight } = ANIMATION.deal;

      const startX = fromPosition?.x ?? 0;
      const startY = fromPosition?.y ?? -150;
      const targetY = 0;

      for (const card of this.cards) {
        card.y = startY;
        card.x = startX;
        card.alpha = 0;
        card.scale.set(0.6 * this.currentScale);
        card.flip(false, false);
      }

      const startTime = Date.now();
      const totalDuration = duration + stagger * (this.cards.length - 1);

      const animate = (): void => {
        const elapsed = Date.now() - startTime;

        for (let i = 0; i < this.cards.length; i++) {
          const card = this.cards[i]!;
          const cardElapsed = Math.max(0, elapsed - i * stagger);
          const progress = Math.min(cardElapsed / duration, 1);
          const eased = easeOutPower2(progress);
          const arcOffset = Math.sin(progress * Math.PI) * arcHeight;

          card.y = startY + (targetY - startY) * eased - arcOffset;
          card.x = startX + (this.basePositions[i]! - startX) * eased;
          card.alpha = Math.min(progress * 4, 1);
          card.scale.set((0.6 + 0.4 * eased) * this.currentScale);
        }

        if (elapsed < totalDuration) {
          requestAnimationFrame(animate);
        } else {
          for (let i = 0; i < this.cards.length; i++) {
            const card = this.cards[i]!;
            card.y = targetY;
            card.x = this.basePositions[i]!;
            card.alpha = 1;
            card.scale.set(this.currentScale);
            card.rotation = this.baseRotations[i]!;
          }

          if (faceUp) {
            setTimeout(async () => {
              const flipPromises: Promise<void>[] = [];
              for (let i = 0; i < this.cards.length; i++) {
                const card = this.cards[i]!;
                flipPromises.push(
                  new Promise<void>((res) => {
                    setTimeout(async () => {
                      await card.flip(true, true);
                      res();
                    }, i * 50);
                  })
                );
              }
              await Promise.all(flipPromises);
              resolve();
            }, 30);
          } else {
            resolve();
          }
        }
      };

      requestAnimationFrame(animate);
    });
  }

  // ===========================================================================
  // PRIVATE - SINGLE CARD ANIMATION (for round-robin dealing)
  // ===========================================================================

  private animateSingleCard(
    cardView: CardView,
    targetX: number,
    targetRotation: number,
    fromPosition?: { x: number; y: number }
  ): Promise<void> {
    return new Promise((resolve) => {
      const { cardFlightDuration: duration, arcHeight } = DEALING_CONFIG;

      const startX = fromPosition?.x ?? 0;
      const startY = fromPosition?.y ?? -150;
      const targetY = 0;

      cardView.x = startX;
      cardView.y = startY;
      cardView.alpha = 0;
      cardView.rotation = 0;
      cardView.scale.set(0.6 * this.currentScale);
      cardView.visible = true;

      const startTime = Date.now();

      const animate = (): void => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOutPower2(progress);
        const arcOffset = Math.sin(progress * Math.PI) * arcHeight;

        cardView.x = startX + (targetX - startX) * eased;
        cardView.y = startY + (targetY - startY) * eased - arcOffset;
        cardView.alpha = Math.min(progress * 3, 1);
        cardView.scale.set((0.6 + 0.4 * eased) * this.currentScale);
        cardView.rotation = targetRotation * eased;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          cardView.x = targetX;
          cardView.y = targetY;
          cardView.alpha = 1;
          cardView.scale.set(this.currentScale);
          cardView.rotation = targetRotation;
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  // ===========================================================================
  // PRIVATE - FOLD ANIMATION
  // ===========================================================================

  private async animateFold(): Promise<void> {
    const { slideDuration } = ANIMATION.fold;

    await Promise.all(this.cards.map((card) => card.flip(false, true)));

    await this.delay(30);
    await this.animateFoldToCenter(slideDuration);

    this.visible = false;
    this.resetCardPositions();
  }

  /**
   * Unified fold animation - movement, scale, rotation, and fade in one smooth motion.
   */
  private animateFoldToCenter(duration: number): Promise<void> {
    return new Promise((resolve) => {
      const { travelDistance, targetScale, targetRotation } = ANIMATION.fold;

      const worldPos = this.getGlobalPosition();
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const tableCenterX = screenWidth / 2;
      const tableCenterY = screenHeight / 2;

      const directionX = tableCenterX - worldPos.x;
      const directionY = tableCenterY - worldPos.y;
      const length = Math.sqrt(directionX * directionX + directionY * directionY);
      const normalizedX = length > 0 ? directionX / length : 0;
      const normalizedY = length > 0 ? directionY / length : 1;

      const targetOffsetX = normalizedX * travelDistance;
      const targetOffsetY = normalizedY * travelDistance;

      // Capture start values for all cards
      const startXs = this.cards.map((c) => c.x);
      const startYs = this.cards.map((c) => c.y);
      const startScales = this.cards.map((c) => c.scale.x);
      const startRotations = this.cards.map((c) => c.rotation);
      const startAlpha = this.cards[0]?.alpha ?? 1;

      // Random variations per card
      const randomRange = (min: number, max: number) => min + Math.random() * (max - min);
      const variations = this.cards.map(() => ({
        rotationMultiplier: randomRange(0.85, 1.15),
        rotationDirection: Math.random() > 0.5 ? 1 : -1,
        spreadX: randomRange(-30, 30),
        spreadY: randomRange(-25, 25),
        travelMultiplier: randomRange(0.8, 1.2),
        scaleOffset: randomRange(-0.05, 0.05),
      }));

      const startTime = Date.now();

      const animate = (): void => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeInPower2(progress);

        for (let i = 0; i < this.cards.length; i++) {
          const card = this.cards[i]!;
          const v = variations[i]!;

          const travelX = targetOffsetX * v.travelMultiplier;
          const travelY = targetOffsetY * v.travelMultiplier;
          const offsetX = travelX + v.spreadX;
          const offsetY = travelY + v.spreadY;
          card.x = startXs[i]! + (offsetX - startXs[i]!) * eased;
          card.y = startYs[i]! + offsetY * eased;

          const scale = startScales[i]! + ((targetScale + v.scaleOffset) - startScales[i]!) * eased;
          card.scale.set(scale);

          const rotation = targetRotation * v.rotationMultiplier * v.rotationDirection;
          card.rotation = startRotations[i]! + rotation * eased;

          const minAlpha = 0.2;
          card.alpha = startAlpha * (1 - progress * (1 - minAlpha));
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  }

  private resetCardPositions(): void {
    for (let i = 0; i < this.cards.length; i++) {
      const card = this.cards[i]!;
      card.y = 0;
      card.x = this.basePositions[i]!;
      card.alpha = 1;
      card.rotation = this.baseRotations[i]!;
      card.scale.set(this.currentScale);
    }
  }

  // ===========================================================================
  // PRIVATE - HIGHLIGHT/GLOW
  // ===========================================================================

  private drawGlow(): void {
    const dims = CARD_STYLES[LAYOUT.cardSize];
    const { glowColor } = ANIMATION.highlight;

    this.glowGraphics.clear();

    const cardCount = this.cards.length;
    const totalSpread = cardCount > 1 ? (cardCount - 1) * this.spacing : 0;
    const glowPadding = 3;
    const totalWidth = dims.width + totalSpread + glowPadding * 2;
    const totalHeight = dims.height + glowPadding * 2;

    for (let i = 2; i >= 0; i--) {
      const expand = i * 2;
      const alpha = 0.15 * (3 - i);
      this.glowGraphics.roundRect(
        -totalWidth / 2 - expand,
        -totalHeight / 2 - expand - ANIMATION.highlight.liftAmount,
        totalWidth + expand * 2,
        totalHeight + expand * 2,
        4 + i
      );
      this.glowGraphics.fill({ color: glowColor, alpha });
    }
  }

  private startPulseAnimation(): void {
    const { pulseFrequency, pulseDuration } = ANIMATION.highlight;
    const startTime = Date.now();

    const pulse = (): void => {
      if (!this.isHighlighted) return;

      const elapsed = Date.now() - startTime;
      const cycleTime = 1000 / pulseFrequency;
      const cycleProgress = (elapsed % cycleTime) / cycleTime;

      const pulseValue = 0.7 + 0.3 * Math.sin(cycleProgress * Math.PI * 2);
      this.glowGraphics.alpha = pulseValue;

      if (elapsed < pulseDuration || this.isHighlighted) {
        this.pulseAnimationId = requestAnimationFrame(pulse);
      }
    };

    pulse();
  }

  private stopPulseAnimation(): void {
    if (this.pulseAnimationId !== null) {
      cancelAnimationFrame(this.pulseAnimationId);
      this.pulseAnimationId = null;
    }
    this.glowGraphics.alpha = 1;
  }

  // ===========================================================================
  // PRIVATE - UTILITIES
  // ===========================================================================

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
